/**
 * Spreaker Parser - Parser per API Spreaker
 * 
 * Recupera metadati episodio dall'API Spreaker e costruisce configurazione
 * per Sasha Player. Gli ads sono già inclusi nello stream URL di Spreaker.
 * 
 * @since v3.3.0 - Integrazione Spreaker
 */

export class SpreakerParser {
  /**
   * Costruttore
   */
  constructor() {
    this.apiBaseUrl = 'https://api.spreaker.com';
  }
  
  /**
   * Fetch e parse dati episodio Spreaker
   * 
   * @param {string|number} episodeId - ID episodio Spreaker
   * @returns {Promise<Object>} Oggetto con dati episodio e configurazione Sasha
   * @throws {Error} Se fetch o parsing fallisce
   */
  async parse(episodeId) {
    try {
      console.log('📡 Fetching Spreaker episode:', episodeId);
      
      // Fetch metadati episodio
      const episodeResponse = await fetch(`${this.apiBaseUrl}/v2/episodes/${episodeId}`);
      if (!episodeResponse.ok) {
        throw new Error(`Spreaker API error: ${episodeResponse.status} ${episodeResponse.statusText}`);
      }
      
      const episodeData = await episodeResponse.json();
      
      // Verifica struttura risposta
      if (!episodeData.response || !episodeData.response.episode) {
        throw new Error('Invalid Spreaker API response structure');
      }
      
      const episode = episodeData.response.episode;
      
      // Usa playback_url dall'API (se disponibile), altrimenti fallback a URL standard
      let streamUrl = episode.playback_url || `https://api.spreaker.com/listen/episode/${episodeId}/http`;
      
      if (episode.playback_url) {
        console.log('✅ Usando playback_url dall\'API:', streamUrl);
      } else {
        console.warn('⚠️ playback_url non disponibile, uso URL standard:', streamUrl);
      }
      
      // Estrai dati
      const configData = this.extractEpisodeData(episode, episodeId, streamUrl);
      
      console.log('✅ Spreaker episode parsed:', configData);
      return configData;
      
    } catch (error) {
      console.error('❌ Spreaker parsing error:', error);
      throw error;
    }
  }
  
  /**
   * Estrai dati episodio e costruisci configurazione Sasha
   * 
   * @param {Object} episode - Oggetto episodio da API Spreaker
   * @param {string|number} episodeId - ID episodio
   * @param {string} streamUrl - URL stream (da endpoint /play o standard)
   * @returns {Object} Configurazione Sasha
   * @private
   */
  extractEpisodeData(episode, episodeId, streamUrl) {
    
    // Estrai description (può essere plain text o HTML)
    let subtitle = '';
    if (episode.description) {
      if (typeof episode.description === 'string') {
        subtitle = episode.description;
      } else if (episode.description.plain) {
        subtitle = episode.description.plain;
      } else if (episode.description.html) {
        // Rimuovi tag HTML per subtitle
        subtitle = episode.description.html.replace(/<[^>]*>/g, '').substring(0, 200);
      }
    }
    
    return {
      // Configurazione audio
      audioUrl: streamUrl,
      
      // Metadati episodio
      title: episode.title || 'Spreaker Episode',
      subtitle: subtitle || '',
      coverImage: episode.image_url || episode.image_original_url || '',
      
      // Metadati aggiuntivi (non usati direttamente ma disponibili)
      duration: episode.duration || 0, // in millisecondi
      episodeId: episode.episode_id || episodeId,
      siteUrl: episode.site_url || '',
      
      // Ads: vuoto perché già inclusi nello stream Spreaker
      ads: [],
      
      // Flag per indicare che è un episodio Spreaker
      _spreaker: true,
      _spreakerEpisodeId: episodeId
    };
  }
  
  /**
   * Verifica se un URL è un episodio Spreaker
   * 
   * @param {string} url - URL da verificare
   * @returns {boolean} True se è un URL Spreaker
   */
  static isSpreakerUrl(url) {
    return url && (
      url.includes('spreaker.com') ||
      url.includes('api.spreaker.com')
    );
  }
  
  /**
   * Estrai episode ID da URL Spreaker
   * 
   * @param {string} url - URL Spreaker
   * @returns {string|null} Episode ID o null
   */
  static extractEpisodeIdFromUrl(url) {
    if (!this.isSpreakerUrl(url)) {
      return null;
    }
    
    // Pattern: /episode/{id} o /listen/episode/{id}
    const match = url.match(/\/episode\/(\d+)/);
    return match ? match[1] : null;
  }
}
